import { ArrowBackRounded, ChevronRightRounded } from "@mui/icons-material";
import { alpha, Box, Button, Typography } from "@mui/material";
import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  color?: string;
  onBack?: () => void;
  backLabel?: string;
  actions?: ReactNode;
}

export default function PageHeader({
  title,
  subtitle,
  icon,
  color = "#6157E0",
  onBack,
  backLabel = "Voltar",
  actions,
}: PageHeaderProps) {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        mb: 3,
        p: { xs: 2.5, md: 3 },
        borderRadius: 4,
        border: "1px solid",
        borderColor: alpha(color, 0.25),
        background: `linear-gradient(135deg, ${alpha(color, 0.14)} 0%, ${alpha(color, 0.04)} 60%, #fff 100%)`,
        "&::after": {
          content: '""',
          position: "absolute",
          width: 220,
          height: 220,
          right: -60,
          top: -90,
          borderRadius: "50%",
          background: alpha(color, 0.1),
          pointerEvents: "none",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 2, position: "relative", zIndex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary", fontSize: 13, fontWeight: 500 }}>
          <Box
            component="button"
            onClick={() => navigate("/painel")}
            sx={{
              all: "unset",
              cursor: "pointer",
              "&:hover": { color: color, textDecoration: "underline" },
              "&:focus-visible": { outline: `2px solid ${color}`, borderRadius: 4 },
            }}
          >
            Início
          </Box>
          <ChevronRightRounded sx={{ fontSize: 16 }} />
          <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
            {title.replace(/^Gestão de /, "")}
          </Box>
        </Box>
        {onBack && (
          <Button
            size="small"
            color="inherit"
            startIcon={<ArrowBackRounded fontSize="small" />}
            onClick={onBack}
            sx={{ color: "text.secondary", bgcolor: "rgba(255,255,255,0.7)" }}
          >
            {backLabel}
          </Button>
        )}
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexWrap: "wrap",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
          {icon && (
            <Box
              sx={{
                width: 56,
                height: 56,
                flexShrink: 0,
                borderRadius: "16px",
                display: "grid",
                placeItems: "center",
                color: "#fff",
                bgcolor: color,
                boxShadow: `0 10px 20px -8px ${alpha(color, 0.8)}`,
                "& svg": { fontSize: 30 },
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" component="h1">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        {actions && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
              "& > .MuiButton-root": { mr: "0 !important" },
            }}
          >
            {actions}
          </Box>
        )}
      </Box>
    </Box>
  );
}
